import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { enhanceChargerWithWebappData } from '../services/webappApi';

const MapContext = createContext(null);

export const useMapContext = () => {
  const context = useContext(MapContext);
  if (!context) {
    throw new Error('useMapContext must be used within MapProvider');
  }
  return context;
};

export const MapProvider = ({ children }) => {
  // Map instance
  const [map, setMap] = useState(null);
  const [mapsApi, setMapsApi] = useState(null);
  
  // Chargers and locations
  const [chargers, setChargers] = useState([]);
  const [filteredChargers, setFilteredChargers] = useState([]);
  const [selectedCharger, setSelectedCharger] = useState(null);
  const [hoveredCharger, setHoveredCharger] = useState(null);
  const [chargerGroup, setChargerGroup] = useState(null);
  
  // Route planning
  const [route, setRoute] = useState(null);
  const [routeWaypoints, setRouteWaypoints] = useState([]);
  const [isRoutePlanning, setIsRoutePlanning] = useState(false);
  const [showRoutePlanner, setShowRoutePlanner] = useState(false);
  const [routePlannerMode, setRoutePlannerMode] = useState('simple'); // 'simple' or 'ev_advanced'
  
  // Filters
  const [activeFilters, setActiveFilters] = useState({
    socketTypes: [],
    speeds: [],
    networks: []
  });
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  
  // UI States
  const [isSearching, setIsSearching] = useState(false);
  const [showChargerInfo, setShowChargerInfo] = useState(false);
  const [mapCenter, setMapCenter] = useState({ lat: 28.6139, lng: 77.2090 }); // Delhi center
  const [mapZoom, setMapZoom] = useState(10);
  const [userLocation, setUserLocation] = useState(null);
  
  // Markers
  const markersRef = useRef([]);
  const routePolylineRef = useRef(null);
  const directionsRendererRef = useRef(null);
  
  // Expose chargers globally for API fallback
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.__MAP_CONTEXT_CHARGERS = chargers;
    }
  }, [chargers]);
  
  // Callbacks
  const clearMarkers = useCallback(() => {
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];
  }, []);
  
  const clearRoute = useCallback(() => {
    if (routePolylineRef.current) {
      routePolylineRef.current.setMap(null);
      routePolylineRef.current = null;
    }
    if (directionsRendererRef.current) {
      directionsRendererRef.current.setMap(null);
      directionsRendererRef.current = null;
    }
    setRoute(null);
    setRouteWaypoints([]);
  }, []);
  
  // Helper function to get charger network
  const getChargerNetwork = useCallback((charger) => {
    const possibleNetworkFields = [
      charger?.name,
      charger?.chargepoint_name,
      charger?.operator,
      charger?.provider,
      charger?.owner,
      charger?.network,
      charger?.team_name,
    ].filter(Boolean);

    for (const networkField of possibleNetworkFields) {
      const normalizedField = networkField.toLowerCase().trim();
      
      // Network mapping
      const networkMap = {
        'zynka': 'zynka',
        'statiq': 'statiq',
        'tata': 'tata',
        'ather': 'ather',
        'iocl': 'iocl',
        'bpcl': 'bpcl',
        'hp': 'hp',
        'jio': 'jio_bp',
        'jiobp': 'jio_bp',
        'jio_bp': 'jio_bp',
        'volttic': 'volttic'
      };

      for (const [key, value] of Object.entries(networkMap)) {
        if (normalizedField.includes(key)) {
          return value;
        }
      }
    }
    
    return 'generic';
  }, []);

  // Filter chargers based on active filters
  useEffect(() => {
    if (!chargers || chargers.length === 0) {
      setFilteredChargers([]);
      return;
    }

    // Apply filters
    const filtered = chargers.filter(charger => {
      // Network filter
      if (activeFilters.networks && activeFilters.networks.length > 0) {
        const chargerNetwork = getChargerNetwork(charger);
        if (!activeFilters.networks.includes(chargerNetwork)) {
          return false;
        }
      }

      // Socket type filter
      if (activeFilters.socketTypes && activeFilters.socketTypes.length > 0) {
        const hasMatchingConnector = charger.connectors?.some(connector => {
          const connectorType = (connector.connectorType || connector.connectorName || connector.type || '').toLowerCase().trim();
          return activeFilters.socketTypes.some(filterType => 
            connectorType.includes(filterType.toLowerCase()) || filterType.toLowerCase().includes(connectorType)
          );
        });
        if (!hasMatchingConnector) {
          return false;
        }
      }

      // Speed filter
      if (activeFilters.speeds && activeFilters.speeds.length > 0) {
        const hasMatchingSpeed = charger.connectors?.some(connector => {
          const power = parseInt(connector.power || connector.powerRating || 0) || 0;
          return activeFilters.speeds.some(speedFilter => {
            if (speedFilter === 'fast' && power >= 50) return true;
            if (speedFilter === 'medium' && power >= 22 && power < 50) return true;
            if (speedFilter === 'slow' && power < 22) return true;
            return false;
          });
        });
        if (!hasMatchingSpeed) {
          return false;
        }
      }

      return true;
    });


    setFilteredChargers(filtered);
  }, [chargers, activeFilters, getChargerNetwork]);

  const handleChargerSelect = useCallback(async (charger, group = null) => {
    
    try {
      // Set basic charger data immediately for responsive UI
      setSelectedCharger(charger);
      setChargerGroup(group);
      setShowChargerInfo(true);
      
      
      // Enhance with webapp data in background
      const enhancedCharger = await enhanceChargerWithWebappData(charger);
      
      // Update with enhanced data
      setSelectedCharger(enhancedCharger);
      
    } catch (error) {
      // Keep original charger data if enhancement fails
      setSelectedCharger(charger);
      setChargerGroup(group);
      setShowChargerInfo(true);
    }
  }, []);
  
  const lastHoverIdRef = useRef(null);
  const lastEnhanceTsRef = useRef(0);
  const HOVER_ENHANCE_MIN_INTERVAL = 1000; // 1s throttle for enhancement calls

  const handleChargerHover = useCallback(async (charger) => {
    if (!charger) {
      lastHoverIdRef.current = null;
      setHoveredCharger(null);
      return;
    }

    const chargerId = charger.id || charger._id || charger.chargepoint_id;
    const now = performance.now();

    // If same charger hovered again within throttle interval: only set if not already
    if (lastHoverIdRef.current === chargerId && now - lastEnhanceTsRef.current < HOVER_ENHANCE_MIN_INTERVAL) {
      return; // Skip redundant hover updates
    }

    // Update basic hover state if changed
    setHoveredCharger(prev => (prev?.id === chargerId ? prev : charger));
    lastHoverIdRef.current = chargerId;

    try {
      lastEnhanceTsRef.current = now;
      const enhancedCharger = await enhanceChargerWithWebappData(charger);
      setHoveredCharger(prev => (prev?.id === chargerId ? enhancedCharger : prev));
    } catch (error) {
    }
  }, []);
  
  const handleCloseChargerInfo = useCallback(() => {
    setShowChargerInfo(false);
    setSelectedCharger(null);
    setChargerGroup(null);
  }, []);
  
  const recenterMap = useCallback(() => {
    if (map && userLocation) {
      map.panTo(userLocation);
      map.setZoom(12);
    } else if (map) {
      map.panTo(mapCenter);
      map.setZoom(mapZoom);
    }
  }, [map, userLocation, mapCenter, mapZoom]);
  
  const zoomIn = useCallback(() => {
    if (map) {
      map.setZoom(map.getZoom() + 1);
    }
  }, [map]);
  
  const zoomOut = useCallback(() => {
    if (map) {
      map.setZoom(map.getZoom() - 1);
    }
  }, [map]);

  const value = {
    // Map
    map,
    setMap,
    mapsApi,
    setMapsApi,
    
    // Chargers
    chargers,
    setChargers,
    filteredChargers,
    setFilteredChargers,
    selectedCharger,
    setSelectedCharger,
    hoveredCharger,
    setHoveredCharger,
    chargerGroup,
    setChargerGroup,
    
    // Route
    route,
    setRoute,
    routeWaypoints,
    setRouteWaypoints,
    isRoutePlanning,
    setIsRoutePlanning,
    showRoutePlanner,
    setShowRoutePlanner,
    routePlannerMode,
    setRoutePlannerMode,
    
    // Filters
    activeFilters,
    setActiveFilters,
    showFilterPanel,
    setShowFilterPanel,
    
    // UI
    isSearching,
    setIsSearching,
    showChargerInfo,
    setShowChargerInfo,
    mapCenter,
    setMapCenter,
    mapZoom,
    setMapZoom,
    userLocation,
    setUserLocation,
    
    // Refs
    markersRef,
    routePolylineRef,
    directionsRendererRef,
    
    // Methods
    clearMarkers,
    clearRoute,
    handleChargerSelect,
    handleChargerHover,
    handleCloseChargerInfo,
    recenterMap,
    zoomIn,
    zoomOut,
  };

  return <MapContext.Provider value={value}>{children}</MapContext.Provider>;
};

export default MapContext;
